import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import { FaExclamationTriangle, FaTrashAlt } from 'react-icons/fa'
import Navbar from '../../Navbar'
import OutletSearch from '../../SelectSearch/OutletSearch'
import Footer from '../../Footer'
import { useNavigate } from 'react-router-dom'
import { formatSalesValue } from '../../../utils/formatUtils'
import { ImSpinner2 } from 'react-icons/im'
import { FaInbox } from 'react-icons/fa6'
import { IoClose } from "react-icons/io5";
import { Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

const PIE_COLORS = [
    "#6366f1", "#ec4899", "#10b981", "#f59e0b", "#3b82f6",
    "#ef4444", "#8b5cf6", "#14b8a6", "#f97316", "#84cc16",
];

const StoreReqsIssued = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const today = new Date().toLocaleDateString('en-CA');
    const token = localStorage.getItem("token");

    const navigate = useNavigate();

    const [selectedOutlet, setSelectedOutlet] = useState();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [storeReqId, setStoreReqId] = useState("");
    const [selectedReq, setSelectedReq] = useState(null);
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);

    // FETCH
    const fetchData = async () => {
        if (!selectedOutlet) return;

        try {
            setLoading(true);
            setError(null);

            const res = await axios.post(`${baseUrl}/getStorereqs`, {
                outlet_name: selectedOutlet,
                from_date: startDate,
                to_date: endDate
            });

            setData(res.data?.data || []);

        } catch (err) {
            console.error(err);
            setError("Failed to load requisitions");
            toast.error(err?.response?.data?.error || err?.response?.data?.message || "Failed to load requisitions");
        } finally {
            setLoading(false);
        }
    };

    const fetchByStoreReqId = async () => {
        if (!storeReqId.trim()) {
            fetchData();
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const res = await axios.post(`${baseUrl}/getStorereqs`, {
                outlet_name: selectedOutlet || "",
                storereqID: storeReqId
            });

            setData(res.data?.data || []);

        } catch (err) {
            console.error(err);
            setError("Failed to load requisition");
            toast.error(
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                "Failed to load requisition"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [selectedOutlet]);

    // ── Unique items + total per requisition row ────────────────────────────────
    const computeUniqueItems = (row) => {
        const items = row.items || [];
        const uniqueNames = new Set(items.map((i) => i.ItemName));
        return uniqueNames.size;
    };

    const computeRowTotal = (row) => {
        const items = row.items || [];
        return items.reduce((sum, item) => {
            const qty = parseFloat(item.Amount) || 0;
            const rate = parseFloat(item.Rate) || 0;
            return sum + qty * rate;
        }, 0);
    };

    // ── Cost center summary (aggregated across all requisitions in view) ───────
    const costCenterSummary = useMemo(() => {
        const map = {};

        data.forEach((row) => {
            const cc = row.CostCenter || "Uncategorized";
            const items = row.items || [];
            const uniqueNames = new Set(items.map((i) => i.ItemName));
            const rowTotal = computeRowTotal(row);

            if (!map[cc]) {
                map[cc] = {
                    costCenter: cc,
                    reqCount: 0,
                    uniqueItems: new Set(),
                    total: 0,
                };
            }

            map[cc].reqCount += 1;
            uniqueNames.forEach((n) => map[cc].uniqueItems.add(n));
            map[cc].total += rowTotal;
        });

        return Object.values(map)
            .map((entry) => ({
                costCenter: entry.costCenter,
                reqCount: entry.reqCount,
                uniqueItemCount: entry.uniqueItems.size,
                total: entry.total,
            }))
            .sort((a, b) => b.total - a.total);
    }, [data]);

    const overallTotal = costCenterSummary.reduce((sum, c) => sum + c.total, 0);

    // ── Pie chart data ────────────────────────────────────────────────────────
    const pieData = useMemo(() => ({
        labels: costCenterSummary.map((c) => c.costCenter),
        datasets: [
            {
                data: costCenterSummary.map((c) => c.total),
                backgroundColor: costCenterSummary.map(
                    (_, i) => PIE_COLORS[i % PIE_COLORS.length]
                ),
                borderColor: "#ffffff",
                borderWidth: 2,
            },
        ],
    }), [costCenterSummary]);

    const pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "bottom",
                labels: {
                    boxWidth: 12,
                    font: { size: 11 },
                },
            },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const value = ctx.parsed || 0;
                        const pct = overallTotal
                            ? ((value / overallTotal) * 100).toFixed(1)
                            : 0;
                        return `${ctx.label}: ${formatSalesValue(value)} (${pct}%)`;
                    },
                },
            },
        },
    };

    // DELETE
    const handleDelete = (id) => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to delete this store requisition #{id}?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        onClick={async () => {
                            toast.dismiss('delete-store-req');
                            setLoading(true);
                            try {
                                await axios.delete(`${baseUrl}/deletestockTransfer_web`, {
                                    data: { store_req_id: id }
                                });

                                toast.success("Store requisition deleted successfully");
                                setStoreReqId("");
                                fetchData();
                            } catch (err) {
                                console.error(err);
                                toast.error(err?.response?.data?.error || err?.response?.data?.message || "Failed to delete store requisition");
                            } finally {
                                setLoading(false)
                            }
                        }}
                    >
                        Delete
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss('delete-store-req')}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'delete-store-req'
            }
        );
    };

    const handleNavigate = (path) => {
        navigate(path);
    };

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className="flex-grow p-3 max-w-6xl mx-auto w-full">

                <div className='flex justify-center items-center mb-3'>
                    <div className='flex-1 flex justify-start gap-3 items-center'>
                        <label className="font-base font-poppins md:text-base">
                            From:
                        </label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="text-xs md:text-base cursor-pointer border rounded px-3 py-2"
                        />
                    </div>

                    <div className='flex-1 flex justify-center'>
                        <OutletSearch
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                            token={token}
                        />
                    </div>

                    <div className='flex-1 flex items-center justify-end gap-3'>
                        <label className="font-base font-poppins md:text-base">
                            To:
                        </label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="text-xs md:text-base cursor-pointer border rounded  px-3 py-2"
                        />
                    </div>
                </div>

                <div className='flex justify-center items-center mb-4'>
                    <div className='flex-1 flex justify-start'>
                        <div className="relative w-52">
                            <input
                                type="text"
                                value={storeReqId}
                                onChange={(e) => setStoreReqId(e.target.value)}
                                onBlur={fetchByStoreReqId}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        fetchByStoreReqId();
                                    }
                                }}
                                placeholder="Search Req ID"
                                className="border rounded px-3 py-2 pr-8 text-sm w-full"
                            />

                            {storeReqId && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setStoreReqId("");
                                        fetchData();
                                    }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition"
                                >
                                    <IoClose size={18} />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className='flex-1 flex justify-center'>
                        <button
                            onClick={fetchData}
                            disabled={loading}
                            className="px-4 py-2 rounded-md font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md text-sm transition"
                        >
                            {loading ? "Loading..." : "View"}
                        </button>
                    </div>

                    <div className='flex-1 flex justify-end gap-3'>
                        <button
                            className='bg-gradient-to-r from-blue-400 to-blue-700 text-white px-3 py-2 text-xs font-semibold rounded-md transition hover:scale-[1.02] hover:shadow-lg active:scale-95'
                            onClick={() => handleNavigate('/create-store-req')}
                        >
                            Create Req
                        </button>
                        <button
                            className='bg-gradient-to-r from-purple-400 to-purple-700 text-white px-3 py-2 text-xs font-semibold rounded-md transition hover:scale-[1.02] hover:shadow-lg active:scale-95'
                            onClick={() => handleNavigate('/costcenter')}
                        >
                            Cost Center
                        </button>
                        <button
                            className='bg-gradient-to-r from-rose-400 to-rose-700 text-white px-3 py-2 text-xs font-semibold rounded-md transition hover:scale-[1.02] hover:shadow-lg active:scale-95'
                            onClick={() => handleNavigate('/wastage-report')}
                        >
                            Wastage
                        </button>
                    </div>
                </div>

                {/* ── Main table + pie chart side by side ─────────────────────── */}

                <div className="bg-white rounded-md shadow-md overflow-hidden flex-1 mb-4">
                    {/* STATE UI */}
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-40 text-gray-500">
                            <ImSpinner2 className="animate-spin text-2xl mb-2" />
                            <p>Loading requisitions...</p>
                        </div>
                    ) : error ? (
                        <div className="flex flex-col items-center justify-center h-40 text-red-500">
                            <FaExclamationTriangle className="text-2xl mb-2" />
                            <p>{error}</p>
                        </div>
                    ) : data.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-40 text-gray-500">
                            <FaInbox className="text-2xl mb-2" />
                            <p>No requisitions found</p>
                        </div>
                    ) : (
                        <div className="overflow-auto max-h-[600px] tiny-scrollbar">
                            <table className="w-full text-sm text-center">
                                <thead className="bg-gray-100 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-2 py-2.5 font-semibold uppercase tracking-wider">Req ID</th>
                                        <th className="px-2 py-2.5 font-semibold uppercase tracking-wider">Cost Center</th>
                                        <th className="px-2 py-2.5 font-semibold uppercase tracking-wider">Issue Date</th>
                                        <th className="px-2 py-2.5 font-semibold uppercase tracking-wider">Unique Items</th>
                                        <th className="px-2 py-2.5 font-semibold uppercase tracking-wider">Total</th>
                                        <th className="px-2 py-2.5 font-semibold uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>

                                <tbody className="divide-y">
                                    {[...data]
                                        .sort(
                                            (a, b) =>
                                                Number(b.idintblStoreRequisition) -
                                                Number(a.idintblStoreRequisition)
                                        )
                                        .map((row) => (
                                            <tr
                                                key={row.idintblStoreRequisition}
                                                className="hover:bg-gray-50 cursor-pointer"
                                                onClick={() => setSelectedReq(row)}
                                            >
                                                <td className="px-4 py-2.5">{row.idintblStoreRequisition}</td>
                                                <td className="px-4 py-2.5">{row.CostCenter}</td>
                                                <td className="px-4 py-2.5">
                                                    {row.formatted_date?.split(" ")[0]}
                                                </td>
                                                <td className="px-4 py-2.5">{computeUniqueItems(row)}</td>
                                                <td className="px-4 py-2.5 font-medium">
                                                    {formatSalesValue(computeRowTotal(row))}
                                                </td>

                                                <td
                                                    className="px-4 py-2.5 flex justify-center"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <button
                                                        onClick={() =>
                                                            handleDelete(row.idintblStoreRequisition)
                                                        }
                                                        className="h-7 w-7 flex items-center justify-center rounded border border-gray-300 text-red-600 hover:bg-red-50"
                                                    >
                                                        <FaTrashAlt className="h-3 w-3" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {!loading && !error && data.length > 0 && (
                    <div className="flex justify-between items-start">
                        {/* ── Cost center summary table ────────────────────────────────── */}
                        <div className="bg-white rounded-md shadow-md overflow-hidden max-w-2xl w-full">
                            <div className="bg-slate-600 text-white px-4 py-2 font-semibold text-sm">
                                Cost Center Summary
                            </div>
                            <table className="w-full text-sm text-center">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="px-3 py-2 font-semibold uppercase tracking-wider text-left">Cost Center</th>
                                        <th className="px-3 py-2 font-semibold uppercase tracking-wider">Requisitions</th>
                                        <th className="px-3 py-2 font-semibold uppercase tracking-wider">Unique Items</th>
                                        <th className="px-3 py-2 font-semibold uppercase tracking-wider">Total</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {costCenterSummary.map((entry) => (
                                        <tr key={entry.costCenter} className="hover:bg-gray-50">
                                            <td className="px-3 py-2 text-left font-medium">{entry.costCenter}</td>
                                            <td className="px-3 py-2">{entry.reqCount}</td>
                                            <td className="px-3 py-2">{entry.uniqueItemCount}</td>
                                            <td className="px-3 py-2 font-medium">
                                                {formatSalesValue(entry.total)}
                                            </td>
                                        </tr>
                                    ))}
                                    <tr className="bg-slate-100 font-semibold">
                                        <td className="px-3 py-2 text-left">Grand Total</td>
                                        <td className="px-3 py-2">
                                            {costCenterSummary.reduce((s, c) => s + c.reqCount, 0)}
                                        </td>
                                        <td className="px-3 py-2">
                                            {costCenterSummary.reduce((sum, entry) => sum + entry.uniqueItemCount, 0)}
                                        </td>
                                        <td className="px-3 py-2">{formatSalesValue(overallTotal)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* ── Pie chart ─────────────────────────────────────────────── */}
                        <div className="bg-white rounded-md shadow-md p-4 w-72 shrink-0">
                            <h3 className="text-sm font-semibold text-gray-700 mb-3 text-center">
                                Cost Center Distribution
                            </h3>
                            <div className="h-64">
                                <Pie data={pieData} options={pieOptions} />
                            </div>
                        </div>
                    </div>
                )}

            </div>

            {/* MODAL */}
            {selectedReq && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg w-[800px] p-4">

                        <h3 className="text-sm font-semibold mb-3">
                            Cost Center - {selectedReq.CostCenter}
                        </h3>

                        <div className="overflow-auto max-h-[300px]">
                            <table className="w-full text-sm text-center">
                                <thead className="bg-gray-100 sticky top-0">
                                    <tr>
                                        <th className="p-2">Item</th>
                                        <th className="p-2">Group</th>
                                        <th className="p-2">UOM</th>
                                        <th className="p-2">Qty</th>
                                        <th className="p-2">Rate</th>
                                        <th className="p-2">Total</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {selectedReq.items.map((item, i) => {
                                        const qty = parseFloat(item.Amount);
                                        const rate = parseFloat(item.Rate);

                                        return (
                                            <tr key={i} className="border-b">
                                                <td className="p-2">{item.ItemName}</td>
                                                <td className="p-2">{item.GroupName || "-"}</td>
                                                <td className="p-2">{item.UOM}</td>
                                                <td className="p-2">{qty}</td>
                                                <td className="p-2">{formatSalesValue(rate)}</td>
                                                <td className="p-2">
                                                    {formatSalesValue(qty * rate)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex justify-end mt-3">
                            <button
                                onClick={() => setSelectedReq(null)}
                                className="px-3 py-1 text-sm bg-gray-500 text-white rounded"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default StoreReqsIssued;